import { useEffect, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { Download, ArrowsClockwise, Copy, ArrowCounterClockwise, UploadSimple, CloudArrowDown } from "@phosphor-icons/react";

export default function Backup({ embedded = false }) {
  const { user: currentUser } = useAuth();
  const [backupToken, setBackupToken] = useState("");
  const [backupTokenRotatedAt, setBackupTokenRotatedAt] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [backupStatus, setBackupStatus] = useState(null);
  const [runningBackup, setRunningBackup] = useState(false);
  const [driveFiles, setDriveFiles] = useState([]);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreText, setRestoreText] = useState("");
  const [restoreFileId, setRestoreFileId] = useState(null);
  const [restoreUpload, setRestoreUpload] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [folderLink, setFolderLink] = useState("");
  const [subfolderName, setSubfolderName] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [folderMode, setFolderMode] = useState("new"); // "new" | "existing"
  const [savingFolder, setSavingFolder] = useState(false);
  const [folderEditOpen, setFolderEditOpen] = useState(false);

  const saveBackupFolder = async () => {
    let payload;
    if (folderMode === "new") {
      if (!newFolderName.trim()) { toast.error("Inserisci il nome della nuova cartella"); return; }
      payload = { new_folder_name: newFolderName.trim() };
    } else {
      if (!folderLink.trim()) { toast.error("Incolla il link o l'ID della cartella"); return; }
      payload = {
        folder_link: folderLink.trim(),
        subfolder_name: subfolderName.trim() || null,
      };
    }
    setSavingFolder(true);
    try {
      const { data } = await api.post("/admin/backup/config/folder", payload);
      toast.success(`Cartella impostata: "${data.folder_name}"`);
      setFolderEditOpen(false);
      setFolderLink(""); setSubfolderName(""); setNewFolderName("");
      loadBackupStatus();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingFolder(false); }
  };

  const RESTORE_TOKEN = "RESTORE_I_UNDERSTAND";

  const loadBackupToken = async () => {
    if (currentUser?.role !== "admin") return;
    setTokenLoading(true);
    try {
      const { data } = await api.get("/admin/backup/token");
      setBackupToken(data.token); setBackupTokenRotatedAt(data.rotated_at || data.created_at);
    } catch (e) { toast.error(formatApiError(e)); } finally { setTokenLoading(false); }
  };

  const rotateBackupToken = async () => {
    if (!window.confirm("Rigenerare il token backup? I cron/curl che usano il token corrente smetteranno di funzionare.")) return;
    setTokenLoading(true);
    try {
      const { data } = await api.post("/admin/backup/token/rotate");
      setBackupToken(data.token); setBackupTokenRotatedAt(data.rotated_at);
      toast.success("Nuovo token backup generato");
    } catch (e) { toast.error(formatApiError(e)); } finally { setTokenLoading(false); }
  };

  const downloadBackup = async (fmt) => {
    try {
      const t = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/admin/backup?format=${fmt}`, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ext = fmt === "bson" ? "tar.gz" : fmt;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url; a.download = `powercom-backup-${ts}.${ext}`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Backup ${fmt.toUpperCase()} scaricato`);
    } catch { toast.error("Errore durante il download del backup"); }
  };

  const copyToken = async () => {
    try { await navigator.clipboard.writeText(backupToken); toast.success("Token copiato negli appunti"); }
    catch { toast.error("Impossibile copiare"); }
  };

  const copyCron = async () => {
    const url = `${API_BASE}/admin/backup?format=json&token=${backupToken}`;
    const cmd = `0 2 * * * curl -sS -o "$HOME/powercom-backup-$(date +\\%Y\\%m\\%d).json" "${url}"`;
    try { await navigator.clipboard.writeText(cmd); toast.success("Comando cron copiato"); }
    catch { toast.error("Impossibile copiare"); }
  };

  const loadBackupStatus = async () => {
    if (currentUser?.role !== "admin") return;
    try {
      const { data } = await api.get("/admin/backup/schedule/status");
      setBackupStatus(data);
      if (data.config?.drive_connected) {
        try {
          const { data: dl } = await api.get("/admin/backup/drive/list");
          setDriveFiles(dl.files || []);
        } catch { setDriveFiles([]); }
      }
    } catch (e) { console.warn("Load backup status failed", e); }
  };

  const connectDrive = async () => {
    try {
      const { data } = await api.get("/admin/backup/oauth/connect");
      window.open(data.authorization_url, "_blank", "noopener");
      toast.info("Autorizza Google Drive nella nuova scheda, poi torna qui e clicca 'Aggiorna stato'");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const disconnectDrive = async () => {
    if (!window.confirm("Scollegare Google Drive? I backup automatici non funzioneranno più.")) return;
    try {
      await api.post("/admin/backup/oauth/disconnect");
      toast.success("Google Drive scollegato"); loadBackupStatus();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runBackupNow = async () => {
    if (!window.confirm("Eseguire ora un backup completo verso Google Drive?")) return;
    setRunningBackup(true);
    try {
      const { data } = await api.post("/admin/backup/schedule/run");
      if (data.status === "success") toast.success(`Backup completato. 3 file caricati, ${data.pruned} vecchi rimossi.`);
      else toast.error(`Backup fallito: ${data.error?.slice(0, 200) || "errore sconosciuto"}`);
      loadBackupStatus();
    } catch (e) { toast.error(formatApiError(e)); } finally { setRunningBackup(false); }
  };

  const doRestore = async () => {
    if (restoreText !== RESTORE_TOKEN) return toast.error("Frase di conferma non corretta");
    setRestoring(true);
    try {
      let res;
      if (restoreFileId) {
        res = await api.post(`/admin/backup/restore/drive/${restoreFileId}?confirm=${RESTORE_TOKEN}`);
      } else if (restoreUpload) {
        const fd = new FormData(); fd.append("file", restoreUpload);
        res = await api.post(`/admin/backup/restore/upload?confirm=${RESTORE_TOKEN}`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
      } else { setRestoring(false); return toast.error("Seleziona un file"); }
      const total = Object.values(res.data.summary || {}).reduce((a, v) => a + v.inserted, 0);
      toast.success(`Restore completato: ${total} record importati. Ricarico l'app...`);
      setRestoreOpen(false);
      setTimeout(() => { window.location.href = "/"; }, 1500);
    } catch (e) { toast.error(formatApiError(e)); } finally { setRestoring(false); }
  };

  useEffect(() => { loadBackupToken(); loadBackupStatus(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [currentUser?.id]);

  if (currentUser?.role !== "admin") return <div className="p-8 text-center text-neutral-500">Accesso riservato agli amministratori.</div>;

  const content = (
    <div className="p-4 md:p-8 space-y-6 max-w-4xl">

        {/* Manual download */}
        <section className="border border-[#002FA7]/30 bg-[#002FA7]/[0.03] p-4 md:p-6" data-testid="manual-backup-section">
          <div className="flex items-start gap-3">
            <Download size={22} weight="bold" className="text-[#002FA7] mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="label-eyebrow text-[#002FA7] mb-1">Backup manuale</div>
              <h3 className="font-display font-bold text-lg tracking-tight">Scarica ora</h3>
              <p className="text-sm text-neutral-700 mt-1">JSON, XLSX (Excel), BSON (mongodump).</p>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button data-testid="backup-download-json" onClick={() => downloadBackup("json")} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#002FA7] hover:bg-[#002480] text-white text-sm font-medium transition-colors"><Download size={16} weight="bold" /> JSON</button>
                <button data-testid="backup-download-xlsx" onClick={() => downloadBackup("xlsx")} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors"><Download size={16} weight="bold" /> XLSX</button>
                <button data-testid="backup-download-bson" onClick={() => downloadBackup("bson")} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-neutral-700 hover:bg-neutral-800 text-white text-sm font-medium transition-colors"><Download size={16} weight="bold" /> BSON</button>
              </div>
              <div className="mt-5 border border-neutral-200 bg-white p-4">
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <div className="label-eyebrow">Token per cron/curl</div>
                  <div className="flex gap-1">
                    <button type="button" data-testid="toggle-token" onClick={() => setShowToken((v) => !v)} className="text-[10px] uppercase tracking-wider text-neutral-500 hover:text-[#002FA7] underline">{showToken ? "Nascondi" : "Mostra"}</button>
                    <button type="button" data-testid="copy-token" onClick={copyToken} disabled={!backupToken} className="p-1 hover:bg-neutral-100 transition-colors text-neutral-500 disabled:opacity-30"><Copy size={14} /></button>
                    <button type="button" data-testid="rotate-token" onClick={rotateBackupToken} disabled={tokenLoading} className="p-1 hover:bg-amber-100 hover:text-amber-700 text-neutral-500 disabled:opacity-30"><ArrowsClockwise size={14} /></button>
                  </div>
                </div>
                <div className="font-mono text-xs bg-neutral-100 border border-neutral-200 px-3 py-2 break-all select-all" data-testid="backup-token-value">
                  {tokenLoading ? "…" : (showToken ? backupToken : (backupToken ? "•".repeat(Math.min(backupToken.length, 40)) : "—"))}
                </div>
                {backupTokenRotatedAt && <div className="text-[10px] text-neutral-500 mt-1 font-mono">Ultima rotazione: {new Date(backupTokenRotatedAt).toLocaleString("it-IT")}</div>}
                <details className="mt-3"><summary className="text-xs text-[#002FA7] cursor-pointer hover:underline">Comando cron da terminale</summary>
                  <div className="mt-2 text-xs space-y-2">
                    <div className="bg-neutral-900 text-emerald-300 font-mono text-[11px] p-2 overflow-x-auto whitespace-pre-wrap break-all">0 2 * * * curl -sS -o &quot;$HOME/powercom-backup-$(date +\%Y\%m\%d).json&quot; &quot;{API_BASE}/admin/backup?format=json&amp;token={backupToken || 'XXXX'}&quot;</div>
                    <button type="button" data-testid="copy-cron" onClick={copyCron} disabled={!backupToken} className="text-xs text-[#002FA7] hover:underline disabled:opacity-40">Copia comando cron</button>
                  </div>
                </details>
              </div>
            </div>
          </div>
        </section>

        {/* Automated backup */}
        <section className="border border-emerald-300 bg-emerald-50 p-4 md:p-6" data-testid="auto-backup-section">
          <div className="flex items-start gap-3">
            <CloudArrowDown size={22} weight="bold" className="text-emerald-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="label-eyebrow text-emerald-800 mb-1">Backup automatico Google Drive</div>
              <h3 className="font-display font-bold text-lg tracking-tight">Programma backup notturno + alert email</h3>
              <p className="text-sm text-neutral-700 mt-1">Ogni notte alle <b>{backupStatus?.config?.schedule || "02:00 (Europe/Rome)"}</b> vengono caricati JSON+XLSX+BSON su Drive. Retention <b>{backupStatus?.config?.retention_days || 30} giorni</b>. Alert email a <b>{backupStatus?.config?.alert_email || "(non configurato)"}</b>.</p>

              {backupStatus?.needs_reauth && (
                <div
                  data-testid="drive-reauth-banner"
                  className="mt-3 border border-red-400 bg-white p-3 flex items-start gap-3"
                >
                  <span className="text-red-600 font-bold text-xl leading-none">⚠</span>
                  <div className="flex-1 text-sm">
                    <div className="font-semibold text-red-800">
                      Token Google Drive scaduto o revocato — riautorizzazione necessaria
                    </div>
                    <div className="mt-1 text-neutral-700">
                      Google ha invalidato il refresh_token (causa più probabile: app OAuth ancora in <i>Testing</i> — i token scadono dopo 7 giorni).
                    </div>
                    <ol className="mt-2 text-xs text-neutral-700 list-decimal pl-5 space-y-1">
                      <li>
                        Pubblica l'app OAuth qui: <a
                          href="https://console.cloud.google.com/apis/credentials/consent"
                          target="_blank" rel="noopener noreferrer"
                          className="text-[#002FA7] underline"
                        >console.cloud.google.com/apis/credentials/consent</a> → <b>PUBBLICA APP</b>
                      </li>
                      <li>Poi clicca qui sotto per ri-collegare Drive.</li>
                    </ol>
                  </div>
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className={`px-2 py-1 border ${backupStatus?.config?.drive_connected ? "border-emerald-500 bg-white text-emerald-800" : "border-red-400 bg-white text-red-700"}`}>{backupStatus?.config?.drive_connected ? "✓ Drive connesso" : "✗ Drive non connesso"}</span>
                <span className={`px-2 py-1 border ${backupStatus?.config?.email_configured ? "border-emerald-500 bg-white text-emerald-800" : "border-amber-400 bg-white text-amber-700"}`}>{backupStatus?.config?.email_configured ? "✓ Alert email attive" : "⚠ Alert email non configurate"}</span>
              </div>

              {/* Destination folder configuration */}
              {backupStatus?.config?.drive_connected && (
                <div className="mt-3 border border-neutral-300 bg-white p-3">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="text-xs">
                      <span className="text-neutral-500 uppercase tracking-wider mr-2">Cartella destinazione:</span>
                      <span className="font-mono break-all" data-testid="backup-folder-id">
                        {backupStatus?.config?.drive_folder_id || "— non impostata —"}
                      </span>
                      {backupStatus?.config?.drive_folder_source === "custom" && (
                        <span className="ml-2 px-1.5 py-0.5 text-[10px] uppercase border border-emerald-400 text-emerald-700 bg-emerald-50">personalizzata</span>
                      )}
                      {backupStatus?.config?.drive_folder_source === "env" && (
                        <span className="ml-2 px-1.5 py-0.5 text-[10px] uppercase border border-neutral-300 text-neutral-600 bg-neutral-50">default env</span>
                      )}
                    </div>
                    <button
                      type="button"
                      data-testid="backup-folder-edit-btn"
                      onClick={() => setFolderEditOpen((v) => !v)}
                      className="text-xs px-2 py-1 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors"
                    >
                      {folderEditOpen ? "Annulla" : "Modifica"}
                    </button>
                  </div>
                  {folderEditOpen && (
                    <div className="mt-2 pt-2 border-t border-neutral-200 space-y-2">
                      {/* Mode toggle */}
                      <div className="flex gap-2 text-xs">
                        <button
                          type="button"
                          data-testid="backup-folder-mode-new"
                          onClick={() => setFolderMode("new")}
                          className={`flex-1 px-2 py-1.5 border transition-colors ${folderMode === "new" ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50"}`}
                        >
                          ✨ Crea nuova cartella
                        </button>
                        <button
                          type="button"
                          data-testid="backup-folder-mode-existing"
                          onClick={() => setFolderMode("existing")}
                          className={`flex-1 px-2 py-1.5 border transition-colors ${folderMode === "existing" ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50"}`}
                        >
                          🔗 Usa cartella esistente
                        </button>
                      </div>

                      {folderMode === "new" ? (
                        <>
                          <div>
                            <label className="text-[11px] uppercase tracking-wider text-neutral-600">
                              Nome o percorso della cartella <span className="text-red-500">*</span>
                            </label>
                            <input
                              data-testid="backup-folder-new-name"
                              value={newFolderName}
                              onChange={(e) => setNewFolderName(e.target.value)}
                              placeholder="es. Backup Applicazioni/Magazzino"
                              className="mt-0.5 w-full px-3 py-1.5 border border-neutral-300 text-xs focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                            />
                          </div>
                          <div className="text-[11px] text-neutral-600 leading-relaxed pt-1">
                            <b>Consigliato.</b> Puoi indicare un semplice nome (es. <code>Magazzino</code>) o un <b>percorso con slash</b> (es. <code>Backup Applicazioni/Magazzino</code>): l'app creerà tutte le cartelle mancanti a partire dalla root del Drive.
                            <br /><br />
                            ⚠️ Se hai già cartelle create manualmente con questi nomi, l'app non le vede (limite scope <code>drive.file</code>) e ne creerà di nuove con lo stesso nome — sono duplicati distinti, indipendenti dalle tue.
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <label className="text-[11px] uppercase tracking-wider text-neutral-600">
                              Link o ID cartella Drive esistente <span className="text-red-500">*</span>
                            </label>
                            <input
                              data-testid="backup-folder-link"
                              value={folderLink}
                              onChange={(e) => setFolderLink(e.target.value)}
                              placeholder="https://drive.google.com/drive/folders/1AbC…  oppure  1AbC…"
                              className="mt-0.5 w-full px-3 py-1.5 border border-neutral-300 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] uppercase tracking-wider text-neutral-600">
                              Nome sotto-cartella <span className="text-neutral-400">(opzionale — sarà creata se non esiste)</span>
                            </label>
                            <input
                              data-testid="backup-folder-subname"
                              value={subfolderName}
                              onChange={(e) => setSubfolderName(e.target.value)}
                              placeholder="es. Backup-2026-Luglio"
                              className="mt-0.5 w-full px-3 py-1.5 border border-neutral-300 text-xs focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                            />
                          </div>
                          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 px-2 py-1.5 leading-relaxed">
                            ⚠️ <b>Attenzione limite scope:</b> l'app può vedere solo cartelle che <b>ha creato lei stessa</b> (scope OAuth <code>drive.file</code>). Se punti a una cartella creata manualmente da te su Drive, otterrai un errore "File not found". Usa piuttosto l'opzione «Crea nuova cartella».
                          </div>
                        </>
                      )}

                      <div className="flex gap-2 pt-1">
                        <button
                          type="button"
                          data-testid="backup-folder-save"
                          disabled={savingFolder || (folderMode === "new" ? !newFolderName.trim() : !folderLink.trim())}
                          onClick={saveBackupFolder}
                          className="px-3 py-1.5 bg-[#002FA7] text-white text-xs uppercase tracking-wider hover:bg-[#0038CC] disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {savingFolder ? "Salvo…" : (folderMode === "new" ? "Crea e salva" : "Salva cartella")}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setFolderEditOpen(false); setFolderLink(""); setSubfolderName(""); setNewFolderName(""); }}
                          className="px-3 py-1.5 border border-neutral-300 text-xs uppercase tracking-wider hover:bg-neutral-100"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                {!backupStatus?.config?.drive_connected ? (
                  <button data-testid="drive-connect" onClick={connectDrive} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">Collega Google Drive</button>
                ) : (<>
                  <button data-testid="run-backup-now" onClick={runBackupNow} disabled={runningBackup} className="px-4 py-2 bg-[#002FA7] hover:bg-[#002480] text-white text-sm font-medium disabled:opacity-50">{runningBackup ? "Backup in corso..." : "▶ Esegui backup ora"}</button>
                  <button data-testid="drive-disconnect" onClick={disconnectDrive} className="px-4 py-2 border border-red-300 hover:bg-red-50 text-red-700 text-sm">Scollega</button>
                </>)}
                <button data-testid="refresh-backup-status" onClick={loadBackupStatus} className="px-4 py-2 border border-neutral-300 hover:bg-white text-sm">Aggiorna stato</button>
              </div>
              {backupStatus?.runs?.length > 0 && (
                <details className="mt-4"><summary className="text-xs text-[#002FA7] cursor-pointer hover:underline">Ultimi backup ({backupStatus.runs.length})</summary>
                  <div className="mt-2 border border-neutral-200 bg-white max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-neutral-100 sticky top-0"><tr className="text-left"><th className="px-2 py-1.5 label-eyebrow">Data</th><th className="px-2 py-1.5 label-eyebrow">Stato</th><th className="px-2 py-1.5 label-eyebrow">File</th><th className="px-2 py-1.5 label-eyebrow">Vecchi rimossi</th><th className="px-2 py-1.5 label-eyebrow">Origine</th></tr></thead>
                      <tbody>{backupStatus.runs.map((r) => (<tr key={r.id} className="border-t border-neutral-100"><td className="px-2 py-1.5 font-mono">{new Date(r.started_at).toLocaleString("it-IT")}</td><td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 text-[10px] ${r.status === "success" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>{r.status}</span></td><td className="px-2 py-1.5 text-neutral-600">{Object.keys(r.uploads || {}).length}/3</td><td className="px-2 py-1.5 text-neutral-600">{r.pruned}</td><td className="px-2 py-1.5 text-neutral-500 text-[10px] truncate max-w-[140px]" title={r.triggered_by}>{r.triggered_by}</td></tr>))}</tbody>
                    </table>
                  </div></details>
              )}
            </div>
          </div>
        </section>

        {/* Restore */}
        <section className="border-2 border-amber-300 bg-amber-50 p-4 md:p-6" data-testid="restore-section">
          <div className="flex items-start gap-3">
            <ArrowCounterClockwise size={22} weight="bold" className="text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="label-eyebrow text-amber-800 mb-1">Restore da backup</div>
              <h3 className="font-display font-bold text-lg tracking-tight">Ripristina i dati da un backup JSON</h3>
              <p className="text-sm text-neutral-700 mt-1"><strong>Attenzione</strong>: il restore <strong>cancella tutti i dati correnti</strong> e li sostituisce con quelli del backup. Il tuo account admin viene preservato.</p>
              <div className="mt-4 space-y-2">
                {driveFiles.length > 0 && (
                  <div>
                    <div className="label-eyebrow mb-1">Da Google Drive (ultimi 20)</div>
                    <div className="border border-neutral-200 bg-white max-h-40 overflow-y-auto">
                      {driveFiles.filter((f) => f.name.endsWith(".json")).slice(0, 20).map((f) => (
                        <button key={f.id} type="button" data-testid={`restore-drive-${f.id}`}
                          onClick={() => { setRestoreFileId(f.id); setRestoreUpload(null); setRestoreOpen(true); setRestoreText(""); }}
                          className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b border-neutral-100 last:border-b-0 text-xs flex items-center justify-between">
                          <span className="font-mono truncate">{f.name}</span>
                          <span className="text-neutral-500 shrink-0 ml-2">{new Date(f.createdTime).toLocaleString("it-IT")}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="label-eyebrow mb-1 mt-2">Da file locale (JSON)</div>
                  <label className="flex items-center gap-2 px-3 py-2 border border-neutral-300 bg-white text-sm cursor-pointer hover:bg-amber-50 max-w-md">
                    <UploadSimple size={16} />
                    <input type="file" accept="application/json,.json" data-testid="restore-file-input"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) { setRestoreUpload(f); setRestoreFileId(null); setRestoreOpen(true); setRestoreText(""); } }}
                      className="hidden" />
                    <span>Scegli file backup JSON...</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </section>

        {restoreOpen && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white border-2 border-amber-400 shadow-xl w-full max-w-lg max-h-[95vh] overflow-y-auto" data-testid="restore-modal">
              <div className="p-5 border-b border-amber-200 bg-amber-50">
                <h2 className="font-display font-bold text-lg tracking-tight text-amber-900">Conferma restore</h2>
              </div>
              <div className="p-5 space-y-4">
                <p className="text-sm text-neutral-700">Stai per <strong>sostituire tutti i dati correnti</strong> con quelli dal backup:</p>
                <div className="bg-neutral-50 border border-neutral-200 p-2 font-mono text-xs">
                  {restoreFileId ? driveFiles.find((f) => f.id === restoreFileId)?.name : restoreUpload?.name}
                </div>
                <p className="text-xs text-neutral-500">Il tuo account admin viene preservato per non perdere l&apos;accesso.</p>
                <div className="bg-neutral-100 border border-neutral-200 p-3 text-xs font-mono select-all">{RESTORE_TOKEN}</div>
                <label className="block">
                  <span className="label-eyebrow block mb-2">Digita la frase per confermare</span>
                  <input autoFocus data-testid="restore-input" value={restoreText} onChange={(e) => setRestoreText(e.target.value)} placeholder={RESTORE_TOKEN} className="w-full px-3 py-2 border border-neutral-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
                </label>
              </div>
              <div className="p-5 pt-0 flex justify-end gap-2 border-t border-neutral-200 mt-4">
                <button data-testid="restore-cancel" onClick={() => setRestoreOpen(false)} disabled={restoring} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100 disabled:opacity-50">Annulla</button>
                <button data-testid="restore-confirm" onClick={doRestore} disabled={restoreText !== RESTORE_TOKEN || restoring} className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white font-semibold uppercase tracking-wider disabled:opacity-40">{restoring ? "Restore in corso..." : "Ripristina"}</button>
              </div>
            </div>
          </div>
        )}
    </div>
  );

  if (embedded) return content;
  return (
    <div>
      <PageHeader title="Backup Database" subtitle="Backup manuale, automatico su Google Drive e restore" />
      {content}
    </div>
  );
}
